<template>
  <div class="app-popup" :class="{ open: item.isOpen, dragging: isDragging }" :style="dragPopupStyle">
    <div
      class="popup-wrap"
      ref="popupRef"
      v-click-outside="() => item.showHandleBar && popupStore.close(props.item.id)"
    >
      <div v-if="item.showHandleBar" class="md-handle" @mousedown="startDrag" @touchstart.prevent="startDrag">
        <span></span>
      </div>

      <div class="popup-con normal-popup">
        <div class="popup-box">
          <div v-if="item.iconSlot" class="icon-area">
            <component :is="item.iconSlot" />
          </div>

          <div class="txt-wrap">
            <h1 class="app-page-tit">{{ item.title }}</h1>
            <p class="sub-txt">{{ item.subTitle }}</p>
          </div>

          <div v-if="item.contentSlot">
            <component :is="item.contentSlot" :payload="item.payload" />
          </div>

          <div class="app-btn-wrap">
            <button
              v-if="item.cancelButton?.text"
              :disabled="item.cancelButton?.disabled"
              type="button"
              class="gray-btn l-btn"
              :class="{ 'full-width-btn': !item.confirmButton?.text }"
              @click="popupStore.cancel(item.id)"
            >
              <span>{{ item.cancelButton.text }}</span>
            </button>

            <button
              v-if="item.confirmButton?.text"
              :disabled="item.confirmButton?.disabled"
              type="button"
              class="primary-btn l-btn"
              :class="{ 'full-width-btn': !item.cancelButton?.text }"
              @click="popupStore.confirm(item.id)"
            >
              <span>{{ item.confirmButton.text }}</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="item.backButton?.text" class="bottom-back-wrap">
        <button type="button" class="close-btn" @click="popupStore.back(item.id)">
          <span>{{ item.backButton.text }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHandleBar } from '@/composables/domains/mobile/useHandleBar';
import { usePopupStore } from '@/stores/popup';
import { PopupItem } from '@/types/components';

const props = defineProps<{ item: PopupItem }>();

const popupStore = usePopupStore();

const popupRef = ref<HTMLElement | null>(null);

const { isDragging, dragPopupStyle, startDrag } = useHandleBar(
  computed(() => props.item.isOpen),
  () => popupStore.close(props.item.id),
  popupRef
);
</script>

<style scoped lang="scss">
.app-popup {
  transform: translateY(100%);
  transition: transform 0.4s cubic-bezier(0.32, 0.72, 0, 1);

  &.open {
    transform: translateY(0);
  }

  &.dragging {
    transition: none;
  }

  &::before {
    content: '';
    position: absolute;
    height: 100%;
    background: $color-transparent-dark60;
    left: 0;
    bottom: 100%;
    right: 0;
  }
}

.md-handle {
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  span {
    transition:
      background 0.2s,
      width 0.2s;
  }
}

.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.3s ease;
}
.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}

.normal-popup {
  .icon-area {
    display: flex;
    justify-content: center;
    padding: 1.25rem 0 0.5rem;
  }

  .txt-wrap {
    text-align: center;

    .sub-txt {
      font-size: $font-size-psmall;
      color: $color-gray-500;
      margin-top: $size-xs;
      white-space: pre-wrap;
    }
  }

  .app-btn-wrap {
    padding-top: 2rem;

    .full-width-btn {
      width: 100%;
    }
  }

  .record-history {
    .day-item {
      margin-top: 1.5rem;
      border-top: 1px solid $color-gray-900;

      li {
        border-bottom: 1px solid $color-gray-40;
        padding: 1rem 0;
        display: flex;
        align-items: center;
        justify-content: space-between;

        span {
          font-size: $font-size-psmall;
        }
      }
    }
  }
}

.normal-popup {
  .icon-area {
    display: flex;
    justify-content: center;
    padding: $size-s 0;
  }

  .txt-wrap {
    text-align: center;

    .sub-txt {
      font-size: $font-size-psmall;
      color: $color-gray-500;
      margin-top: $size-xs;
    }
  }

  .app-btn-wrap {
    padding-top: 2rem;
  }
}

.bottom-back-wrap {
  display: flex;
  justify-content: center;
  margin-top: 1rem;

  .close-btn {
    span {
      color: #fff;
    }
  }
}
</style>
